import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, UserCheck, Phone, MapPin, Building2, Shield, Search, X, UserCircle2, UserCircle } from 'lucide-react';
import Button from '../../components/UI/Button';
import Input from '../../components/UI/Input';
import { TableRoot, Table, THead, TR, TH, TBody, TD } from '../../components/UI/Table';
import LoadingSpinner from '../../components/UI/LoadingSpinner';
import { Controller, Region, Office } from '../../lib/sdk/index';
import { toast } from 'react-toastify';
import { formatDate } from '../../lib/utils/formatting';
import { useControllers, useRegions, useOffices, useCreateController, useUpdateController, useDeleteController } from '../../hooks/useQueries';
import { getErrorMessage } from '../../lib/utils/errorMessage';

const useDebounce = <T,>(value: T, delay: number): T => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const timer = window.setTimeout(() => setDebouncedValue(value), delay);
    return () => window.clearTimeout(timer);
  }, [value, delay]);

  return debouncedValue;
};

const ManageControllers: React.FC = () => {
  const [page, setPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearchTerm = useDebounce(searchTerm, 350);
  const limit = 50;
  
  // Use React Query hooks with pagination
  const { data: controllersResponse, isLoading: controllersLoading, refetch: refetchControllers } = useControllers({ 
    page, 
    limit, 
    search: debouncedSearchTerm || undefined 
  });
  const { data: regionsResponse = [] } = useRegions();
  const { data: officesResponse = [] } = useOffices();
  
  const controllers = Array.isArray(controllersResponse?.data) ? controllersResponse.data : [];
  const pagination = controllersResponse?.pagination;
  const regions = Array.isArray(regionsResponse?.data) ? regionsResponse.data : (Array.isArray(regionsResponse) ? regionsResponse : []);
  const offices = Array.isArray(officesResponse?.data) ? officesResponse.data : (Array.isArray(officesResponse) ? officesResponse : []);
  const loading = controllersLoading;
  
  // Reset to page 1 when search term changes
  useEffect(() => {
    setPage(1);
  }, [debouncedSearchTerm]);
  
  const [showForm, setShowForm] = useState(false);
  const [editingController, setEditingController] = useState<Controller | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteControllerId, setDeleteControllerId] = useState<string | null>(null);
  const [formData, setFormData] = useState<{
    name: string;
    cnic: string;
    phone: string;
    fatherName: string;
    address: string;
  }>({
    name: '',
    cnic: '',
    phone: '',
    fatherName: '',
    address: ''
  });
  const [errors, setErrors] = useState<{[key: string]: string}>({});

  // Mutations
  const createControllerMutation = useCreateController({
    onSuccess: () => {
      toast.success('Controller created successfully!');
      setShowForm(false);
      setEditingController(null);
      setFormData({ name: '', cnic: '', phone: '', fatherName: '', address: '' });
      setErrors({});
      refetchControllers();
    },
    onError: (error: any) => {
      console.error('Error creating controller:', error);
      toast.error(getErrorMessage(error, 'Failed to create controller'));
    }
  });

  const updateControllerMutation = useUpdateController({
    onSuccess: () => {
      toast.success('Controller updated successfully!');
      setShowForm(false);
      setEditingController(null);
      setFormData({ name: '', cnic: '', phone: '', fatherName: '', address: '' });
      setErrors({});
      refetchControllers();
    },
    onError: (error: any) => {
      console.error('Error updating controller:', error);
      toast.error(getErrorMessage(error, 'Failed to update controller'));
    }
  });

  const deleteControllerMutation = useDeleteController({
    onSuccess: () => {
      toast.success('Controller deleted successfully!');
      setShowDeleteConfirm(false);
      setDeleteControllerId(null);
      refetchControllers();
    },
    onError: (error: any) => {
      console.error('Error deleting controller:', error);
      toast.error(getErrorMessage(error, 'Failed to delete controller'));
      setShowDeleteConfirm(false);
      setDeleteControllerId(null);
    }
  });


  // Validation functions
  const validateCNIC = (cnic: string): boolean => {
    const cnicPattern = /^\d{5}-\d{7}-\d$/;
    return cnicPattern.test(cnic);
  };

  const validatePhone = (phone: string): boolean => {
    // Remove +92- prefix for validation
    const cleanPhone = phone.replace(/^\+92-/, '');
    // Check if it's exactly 10 digits after removing prefix
    return /^\d{10}$/.test(cleanPhone);
  };

  const validateForm = (): boolean => {
    const newErrors: {[key: string]: string} = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }

    if (!formData.cnic.trim()) {
      newErrors.cnic = 'CNIC is required';
    } else if (!validateCNIC(formData.cnic)) {
      newErrors.cnic = 'CNIC must be in format: 35202-1234567-1';
    }

    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone number is required';
    } else if (!validatePhone(formData.phone)) {
      newErrors.phone = 'Please enter a valid Pakistani phone number';
    }

    if (!formData.fatherName.trim()) {
      newErrors.fatherName = 'Father name is required';
    }

    if (!formData.address.trim()) {
      newErrors.address = 'Address is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Clear previous errors
    setErrors({});
    
    // Validate form before submission
    if (!validateForm()) {
      return;
    }

    try {
      if (editingController) {
        updateControllerMutation.mutate({
          id: editingController._id,
          data: formData
        });
      } else {
        createControllerMutation.mutate(formData);
      }
    } catch (error: any) {
      // Display the specific error message from the backend
      toast.error(getErrorMessage(error, 'An unexpected error occurred'));
    }
  };

  const handleEdit = (controller: Controller) => {
    setEditingController(controller);
    setFormData({
      name: controller.name,
      cnic: controller.cnic,
      phone: controller.phone,
      fatherName: controller.fatherName,
      address: controller.address
    });
    setShowForm(true);
    setErrors({});
  };

  const handleDelete = async (id: string) => {
    setDeleteControllerId(id);
    setShowDeleteConfirm(true);
  };

  const confirmDelete = () => {
    if (!deleteControllerId) return;
    deleteControllerMutation.mutate(deleteControllerId);
  };

  const cancelDelete = () => {
    setShowDeleteConfirm(false);
    setDeleteControllerId(null);
  };

  const resetForm = () => {
    setFormData({ name: '', cnic: '', phone: '', fatherName: '', address: '' });
    setEditingController(null);
    setShowForm(false);
    setErrors({});
  };

  // Format CNIC input (add dashes automatically)
  const handleCNICChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let value = e.target.value.replace(/[^0-9]/g, ''); // Remove non-digits
    
    if (value.length <= 5) {
      // First part: 5 digits
      setFormData({ ...formData, cnic: value });
    } else if (value.length <= 12) {
      // Second part: 7 digits
      setFormData({ ...formData, cnic: `${value.slice(0, 5)}-${value.slice(5)}` });
    } else if (value.length <= 13) {
      // Third part: 1 digit
      setFormData({ ...formData, cnic: `${value.slice(0, 5)}-${value.slice(5, 12)}-${value.slice(12)}` });
    } else {
      // Limit to 13 digits total
      setFormData({ ...formData, cnic: `${value.slice(0, 5)}-${value.slice(5, 12)}-${value.slice(12, 13)}` });
    }
    
    // Clear CNIC error when user types
    if (errors.cnic) {
      setErrors({ ...errors, cnic: '' });
    }
  };

  // Format phone input (add +92 automatically)
  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let value = e.target.value;
    
    // If user is typing and already has +92-, don't add it again
    if (value.startsWith('+92-')) {
      // Extract only the digits after +92-
      const digits = value.replace(/^\+92-/, '').replace(/[^0-9]/g, '');
      
      // Limit to 10 digits
      if (digits.length <= 10) {
        setFormData({ ...formData, phone: `+92-${digits}` });
      } else {
        setFormData({ ...formData, phone: `+92-${digits.slice(0, 10)}` });
      }
    } else {
      // If user is typing without +92-, add it automatically
      const digits = value.replace(/[^0-9]/g, '');
      
      if (digits.length === 0) {
        setFormData({ ...formData, phone: '' });
      } else if (digits.length <= 10) {
        setFormData({ ...formData, phone: `+92-${digits}` });
      } else {
        setFormData({ ...formData, phone: `+92-${digits.slice(0, 10)}` });
      }
    }
    
    // Clear phone error when user types
    if (errors.phone) {
      setErrors({ ...errors, phone: '' });
    }
  };


  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Manage Controllers</h1>
          <p className="text-gray-600 dark:text-gray-400">Add, edit, and delete controllers</p>
        </div>
        <Button onClick={() => setShowForm(true)} className="flex items-center gap-2">
          <Plus className="h-4 w-4" />
          Add Controller
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="card">
          <div className="flex items-center">
            <div className="p-2 bg-blue-100 dark:bg-blue-900 rounded-lg">
              <UserCircle  className="h-6 w-6 text-blue-600 dark:text-blue-400" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Total Controllers</p>
              <p className="text-2xl font-semibold text-gray-900 dark:text-gray-100">{pagination?.total || controllers.length}</p>
            </div>
          </div>
        </div>
        
        <div className="card">
          <div className="flex items-center">
            <div className="p-2 bg-green-100 dark:bg-green-900 rounded-lg">
              <MapPin className="h-6 w-6 text-green-600 dark:text-green-400" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Regional Controllers</p>
              <p className="text-2xl font-semibold text-gray-900 dark:text-gray-100">
                {regions.filter(region => region.controllerId).length}
              </p>
            </div>
          </div>
        </div>
        
        <div className="card">
          <div className="flex items-center">
            <div className="p-2 bg-purple-100 dark:bg-purple-900 rounded-lg">
              <Building2 className="h-6 w-6 text-purple-600 dark:text-purple-400" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Office Controllers</p>
              <p className="text-2xl font-semibold text-gray-900 dark:text-gray-100">
                {new Set(offices.filter(office => office.controllerId).map(office => 
                  typeof office.controllerId === 'string' ? office.controllerId : office.controllerId._id
                )).size}
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="card">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
          <Input
            type="text"
            placeholder="Search controllers by name, CNIC, or phone..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
      </div>

      <div className="card">
        {controllers.length === 0 ? (
          <div className="text-center py-12">
            <UserCheck className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-gray-100">No controllers found</h3>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              Get started by adding your first controller.
            </p>
          </div>
        ) : (
          <>
            <TableRoot>
              <Table>
                <THead>
                  <TR>
                    <TH>Name</TH>
                    <TH>CNIC</TH>
                    <TH>Phone</TH>
                    <TH>Father Name</TH>
                    <TH>Address</TH>
                    <TH>Created Date</TH>
                    <TH>Updated Date</TH>
                    <TH>Actions</TH>
                  </TR>
                </THead>
                <TBody>
                  {controllers.map((controller) => (
                    <TR key={controller._id}>
                      <TD className="font-medium">{controller.name}</TD>
                      <TD>{controller.cnic}</TD>
                      <TD>{controller.phone}</TD>
                      <TD>{controller.fatherName}</TD>
                      <TD className="max-w-xs truncate" title={controller.address}>
                        {controller.address}
                      </TD>
                      <TD>{formatDate(controller.createdAt || controller.created_at)}</TD>
                      <TD>{formatDate(controller.updatedAt || controller.updated_at)}</TD>
                      <TD>
                        <div className="flex space-x-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleEdit(controller)}
                            className="flex items-center gap-1"
                          >
                            <Edit className="h-4 w-4" />
                            Edit
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleDelete(controller._id)}
                            className="flex items-center gap-1 text-red-600 hover:text-red-700"
                          >
                            <Trash2 className="h-4 w-4" />
                            Delete
                          </Button>
                        </div>
                      </TD>
                    </TR>
                  ))}
                </TBody>
              </Table>
            </TableRoot>
            
            {/* Pagination */}
            {pagination && pagination.totalPages > 1 && (
              <div className="flex items-center justify-between mt-4 px-4 py-3 bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700">
                <div className="text-sm text-gray-700 dark:text-gray-300">
                  Showing {((pagination.page - 1) * pagination.limit) + 1} to {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total} controllers
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                    disabled={pagination.page === 1 || loading}
                  >
                    Previous
                  </Button>
                  <span className="flex items-center px-4 text-sm text-gray-700 dark:text-gray-300">
                    Page {pagination.page} of {pagination.totalPages}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage(p => Math.min(pagination.totalPages, p + 1))}
                    disabled={pagination.page === pagination.totalPages || loading}
                  >
                    Next
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-md">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
                {editingController ? 'Edit Controller' : 'Add Controller'}
              </h2>
              <Button
                variant="ghost"
                size="sm"
                onClick={resetForm}
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="h-5 w-5" />
              </Button>
            </div>
            
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="form-label">Name</label>
                <Input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  placeholder="Enter controller name"
                  className={errors.name ? 'border-red-500' : ''}
                />
                {errors.name && <p className="text-red-500 text-xs mt-1">{errors.name}</p>}
              </div>
              
              <div>
                <label className="form-label">CNIC</label>
                <Input
                  type="text"
                  value={formData.cnic}
                  onChange={handleCNICChange}
                  required
                  placeholder="35202-1234567-1"
                  maxLength={15}
                  className={errors.cnic ? 'border-red-500' : ''}
                />
                <p className="text-xs text-gray-500 mt-1">Format: XXXXX-XXXXXXX-X</p>
                {errors.cnic && <p className="text-red-500 text-xs mt-1">{errors.cnic}</p>}
              </div>
              
              <div>
                <label className="form-label">Phone Number</label>
                <Input
                  type="tel"
                  value={formData.phone}
                  onChange={handlePhoneChange}
                  required
                  placeholder="+92-300-1234567"
                  maxLength={15}
                  className={errors.phone ? 'border-red-500' : ''}
                />
                <p className="text-xs text-gray-500 mt-1">Format: +92-XXX-XXXXXXX</p>
                {errors.phone && <p className="text-red-500 text-xs mt-1">{errors.phone}</p>}
              </div>
              
              <div>
                <label className="form-label">Father Name</label>
                <Input
                  type="text"
                  value={formData.fatherName}
                  onChange={(e) => setFormData({ ...formData, fatherName: e.target.value })}
                  required
                  placeholder="Enter father's name"
                  className={errors.fatherName ? 'border-red-500' : ''}
                />
                {errors.fatherName && <p className="text-red-500 text-xs mt-1">{errors.fatherName}</p>}
              </div>
              
              <div>
                <label className="form-label">Address</label>
                <textarea
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  required
                  placeholder="Enter full address"
                  className={`form-input ${errors.address ? 'border-red-500' : ''}`}
                  rows={3}
                />
                {errors.address && <p className="text-red-500 text-xs mt-1">{errors.address}</p>}
              </div>
              
              <div className="flex space-x-3 pt-4">
                <Button type="submit" className="flex-1">
                  {editingController ? 'Update Controller' : 'Add Controller'}
                </Button>
                <Button type="button" variant="outline" onClick={resetForm} className="flex-1">
                  Cancel
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-md">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-4">Confirm Deletion</h2>
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              Are you sure you want to delete this controller? This action cannot be undone.
            </p>
            <div className="flex justify-end space-x-3">
              <Button variant="outline" onClick={cancelDelete} className="flex-1">
                Cancel
              </Button>
                             <Button variant="error" onClick={confirmDelete} className="flex-1">
                 Delete
               </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ManageControllers;
